'use client'

import { useEffect, useRef, useState } from 'react'

import type { PathRow } from '@/features/landing/path/path-copy'
import { WrittenGlyph } from '@/features/landing/path/written-glyph'

export type PathRowWithStrokes = PathRow & {
    strokePaths: string[]
    viewBox: string
}

// Spec §3.2: four rows under one hairline — glyph, name, the doorway's own
// verb, a link into the Marginalia where one exists, one insight. No state
// words, no cursor, no vermilion; the glyph alone carries the state.
// Reached = hover on desktop, the scroll band on touch. Below lg the
// insight stacks under the verb (at md the three columns left it about
// 24 characters wide); from lg it has its own column.
export function PathRows({ rows }: { rows: PathRowWithStrokes[] }) {
    const [reached, setReached] = useState<number | null>(null)
    const refs = useRef<Array<HTMLLIElement | null>>([])

    useEffect(() => {
        if (!window.matchMedia('(hover: none)').matches) return
        const inBand = new Set<number>()
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    const i = refs.current.indexOf(e.target as HTMLLIElement)
                    if (i < 0) continue
                    if (e.isIntersecting) inBand.add(i)
                    else inBand.delete(i)
                }
                setReached(inBand.size ? Math.min(...inBand) : null)
            },
            { rootMargin: '-40% 0px -40% 0px' }
        )
        for (const el of refs.current) if (el) io.observe(el)
        return () => io.disconnect()
    }, [])

    return (
        <ol
            aria-label="Four progressive learning paths"
            className="mx-auto max-w-5xl border-t border-rule"
            onMouseLeave={() => setReached(null)}
        >
            {rows.map((row, i) => (
                <li
                    key={row.title}
                    ref={(el) => {
                        refs.current[i] = el
                    }}
                    onMouseEnter={() => setReached(i)}
                    className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1 border-b border-rule py-7 md:grid-cols-[4.6rem_minmax(0,1fr)] md:gap-x-9 md:py-9 lg:grid-cols-[4.6rem_19rem_minmax(0,1fr)]"
                >
                    <WrittenGlyph
                        strokePaths={row.strokePaths}
                        viewBox={row.viewBox}
                        open={row.open}
                        reached={reached === i}
                        className="size-10 md:size-[46px]"
                    />
                    <div>
                        <h3 className="font-serif text-lp-title text-ink">
                            {row.title}
                        </h3>
                        <p className="mt-1.5 font-serif text-lp-copy text-body-fg">
                            {row.verb}
                        </p>
                        {row.more ? (
                            <a
                                href={row.more.href}
                                className="mt-2 inline-block border-b border-ink/30 font-serif text-lp-caption text-muted-fg italic hover:text-ink"
                            >
                                {row.more.label}
                            </a>
                        ) : null}
                    </div>
                    <p className="col-span-2 mt-2.5 max-w-[58ch] font-serif text-lp-copy text-body-fg md:col-span-1 md:col-start-2 md:mt-3 lg:col-start-auto lg:mt-0 lg:border-l lg:border-rule-soft lg:pl-9">
                        <b className="font-medium text-ink">
                            {row.insight.lead}
                        </b>{' '}
                        {row.insight.rest}
                    </p>
                </li>
            ))}
        </ol>
    )
}
